import React, { useState, useRef } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { 
  Upload, 
  FileText, 
  Image, 
  FileSpreadsheet, 
  File as FileIcon,
  X, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Download,
  Eye,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FileUpload = ({ user, token, onSolutionReceived, selectedLevel, setSelectedLevel }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractedContent, setExtractedContent] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [showExtractedContent, setShowExtractedContent] = useState(false);
  const fileInputRef = useRef(null);
  
  // Configuration des types de fichiers
  const fileTypes = {
    images: { 
      extensions: ['.jpg', '.jpeg', '.png', '.bmp', '.gif', '.tiff', '.webp'],
      icon: <Image className="w-4 h-4" />,
      color: 'bg-blue-500',
      description: 'Images avec OCR'
    },
    documents: { 
      extensions: ['.pdf', '.docx'],
      icon: <FileText className="w-4 h-4" />,
      color: 'bg-red-500',
      description: 'PDF et Word'
    },
    spreadsheets: { 
      extensions: ['.xlsx', '.xls'],
      icon: <FileSpreadsheet className="w-4 h-4" />,
      color: 'bg-green-500',
      description: 'Excel'
    },
    text: { 
      extensions: ['.txt'],
      icon: <FileIcon className="w-4 h-4" />,
      color: 'bg-gray-500',
      description: 'Fichiers texte'
    }
  };

  const getFileTypeInfo = (filename) => {
    const extension = '.' + filename.split('.').pop().toLowerCase();
    
    for (const [type, info] of Object.entries(fileTypes)) {
      if (info.extensions.includes(extension)) {
        return { type, ...info };
      }
    }
    
    return { 
      type: 'unknown', 
      icon: <FileIcon className="w-4 h-4" />, 
      color: 'bg-gray-500',
      description: 'Type inconnu'
    };
  };

  const validateFile = (file) => {
    const maxSize = 20 * 1024 * 1024; // 20 MB
    
    if (!file) {
      return { valid: false, error: "Aucun fichier sélectionné" };
    }
    
    if (file.size > maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return { valid: false, error: `Fichier trop volumineux (${sizeMB} MB). Limite : 20 MB` };
    }
    
    const fileInfo = getFileTypeInfo(file.name);
    if (fileInfo.type === 'unknown') {
      return { 
        valid: false, 
        error: `Type de fichier non supporté. Types acceptés : images, PDF, Word, Excel, texte` 
      };
    }
    
    return { valid: true, fileInfo };
  };

  const handleFileSelect = (file) => {
    const validation = validateFile(file);
    
    if (!validation.valid) {
      setError(validation.error);
      setSelectedFile(null);
      return;
    }
    
    setSelectedFile(file);
    setError("");
    setSuccess("");
    setExtractedContent("");
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setError("");
    setSuccess("");
    setExtractedContent("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadAndSolve = async () => {
    if (!selectedFile) {
      setError("Veuillez sélectionner un fichier");
      return;
    }
    
    if (!selectedLevel) {
      setError("Veuillez choisir votre niveau d'étude");
      return;
    }
    
    setIsUploading(true);
    setError("");
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('level', selectedLevel);
      formData.append('detailed', 'true');
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      };
      
      const response = await axios.post(`${API}/upload-file`, formData, config);
      
      setSuccess(`✅ Fichier traité avec succès !`);
      setExtractedContent(response.data.extracted_content);
      
      // Appeler le callback avec la solution reçue
      // La réponse contient directement les champs (pas de wrapper "solution")
      if (onSolutionReceived) {
        onSolutionReceived(response.data);
      }
      
      // Réinitialiser le formulaire après succès
      clearFile();
      
    } catch (error) {
      console.error("Erreur upload:", error);
      
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          "Erreur lors du traitement du fichier";
      
      setError(`❌ ${errorMessage}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Card className="bg-gray-900/50 border-green-800/30 backdrop-blur-md">
      <CardHeader>
        <CardTitle className="text-xl text-green-400 flex items-center">
          <Upload className="w-5 h-5 mr-2" />
          📎 Upload de fichiers
        </CardTitle>
        <p className="text-green-300 text-sm">
          Uploadez vos exercices en image, PDF, Word ou Excel pour une résolution automatique
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Zone de drop */}
        <div 
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive 
              ? 'border-green-400 bg-green-950/20' 
              : 'border-green-700/50 hover:border-green-600/70'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            accept=".jpg,.jpeg,.png,.bmp,.gif,.tiff,.webp,.pdf,.docx,.xlsx,.xls,.txt"
          />
          
          {selectedFile ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center space-x-3">
                <div className={`p-2 rounded ${getFileTypeInfo(selectedFile.name).color}`}>
                  {getFileTypeInfo(selectedFile.name).icon}
                </div>
                <div className="text-left">
                  <p className="text-green-300 font-medium">{selectedFile.name}</p>
                  <p className="text-green-400 text-sm">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB - {getFileTypeInfo(selectedFile.name).description}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFile}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="p-4 bg-green-950/30 rounded-full">
                  <Upload className="w-8 h-8 text-green-400" />
                </div>
              </div>
              
              <div>
                <h3 className="text-green-300 text-lg font-medium">
                  Glissez-déposez votre fichier ici
                </h3>
                <p className="text-green-400 text-sm mt-1">
                  ou cliquez pour sélectionner un fichier
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-2">
                {Object.entries(fileTypes).map(([type, info]) => (
                  <Badge 
                    key={type} 
                    variant="outline" 
                    className="border-green-700 text-green-300"
                  >
                    {info.icon}
                    <span className="ml-1 text-xs">{info.description}</span>
                  </Badge>
                ))}
              </div>
              
              <p className="text-green-500 text-xs">
                ⚡ Limite : 20 MB • 🔒 Stockage temporaire (suppression automatique)
              </p>
            </div>
          )}
        </div>
        
        {/* Sélection du niveau */}
        <div className="space-y-2">
          <label className="text-green-300 text-sm font-medium">Niveau d'étude</label>
          <Select value={selectedLevel} onValueChange={setSelectedLevel}>
            <SelectTrigger className="bg-black/60 border-green-700/50 text-green-100">
              <SelectValue placeholder="Choisissez votre niveau" />
            </SelectTrigger>
            <SelectContent className="bg-black border-green-700">
              <SelectItem value="collège" className="text-green-100 hover:bg-green-950">
                Collège (6ème-3ème)
              </SelectItem>
              <SelectItem value="lycée" className="text-green-100 hover:bg-green-950">
                Lycée (2nde-Terminale)
              </SelectItem>
              <SelectItem value="université" className="text-green-100 hover:bg-green-950">
                Université (L1-M2)
              </SelectItem>
              <SelectItem value="recherche" className="text-green-100 hover:bg-green-950">
                Recherche (Doctorat+)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Messages d'erreur/succès */}
        {error && (
          <div className="bg-red-950/20 border border-red-800/30 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-red-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-950/20 border border-green-800/30 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-green-400">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">{success}</span>
            </div>
          </div>
        )}
        
        {/* Barre de progression */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-300">Traitement en cours...</span>
              <span className="text-green-400">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Contenu extrait (optionnel) */}
        {extractedContent && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-green-400 font-medium">Contenu extrait du fichier :</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowExtractedContent(!showExtractedContent)}
                className="text-green-300 hover:text-green-200"
              >
                {showExtractedContent ? <Eye className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showExtractedContent ? 'Masquer' : 'Afficher'}
              </Button>
            </div>
            
            {showExtractedContent && (
              <div className="bg-black/40 border border-green-800/30 rounded-lg p-4 max-h-60 overflow-y-auto">
                <pre className="text-green-200 text-sm whitespace-pre-wrap">
                  {extractedContent}
                </pre>
              </div>
            )}
          </div>
        )}
        
        {/* Bouton d'action */}
        <Button
          onClick={handleUploadAndSolve}
          disabled={!selectedFile || isUploading || !selectedLevel}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-semibold h-12"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Traitement...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Analyser et résoudre le fichier
            </>
          )}
        </Button>
        
        {/* Info utilisateur */}
        {user && (
          <div className="text-center text-sm text-green-400">
            💡 <strong>Crédits restants :</strong> {user.free_credits} • 
            {user.is_premium ? (
              <span className="text-green-300"> 👑 Premium actif</span>
            ) : (
              <span className="text-orange-300"> Basique</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};